// ============================================================
// Shared Interfaces & Types
// ============================================================

import { CardRating, SRAlgorithm, FlashcardFormat, FlashcardType, SRState } from '../enums';

/** SR card state persisted in DB */
export interface SRCardState {
  cardId: string;
  algorithm: SRAlgorithm;
  state: SRState;
  step: number;
  ease: number;
  interval: number;
  repetitions: number;
  dueDate: Date;
  lastReviewDate: Date | null;
  stability: number;
  difficulty: number;
  lapses: number;
  elapsedDays: number;
}

/** Result of a spaced repetition review */
export interface SRReviewResult {
  cardId: string;
  newState: SRCardState;
  nextReviewDate: Date;
  intervalDays: number;
  isRelearning: boolean;
}

/** Generic API error */
export interface ApiError {
  statusCode: number;
  message: string;
  error: string;
  details?: unknown[];
}

/** Keycloak token response */
export interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  'not-before-policy'?: number;
  session_state?: string;
  scope?: string;
}

/** WebSocket event payloads */
export interface WSStudySessionUpdate {
  sessionId: string;
  reviewedCards: number;
  totalCards: number;
  currentCardId: string | null;
}

export interface WSNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: Date;
  isRead: boolean;
}

/** Parsed flashcard from import */
export interface ParsedFlashcard {
  front: string;
  back: string;
  extra?: string;
  tags?: string[];
  type: FlashcardType;
  format: FlashcardFormat;
  clozeText?: string;
  clozeDeletions?: number[];
  mediaUrls?: string[];
  /** Names of source files referenced by this flashcard (from Anki import) */
  sourceLinkNames?: string[];
  /** Multiple choice options (for MULTIPLE_CHOICE type) */
  multipleChoiceOptions?: MultipleChoiceOption[];
}

/** Filter options for querying flashcards */
export interface FlashcardFilter {
  deckIds?: string[];
  tags?: string[];
  dueOnly?: boolean;
  search?: string;
  type?: FlashcardType;
}

/** Study queue item */
export interface StudyQueueItem {
  cardId: string;
  deckId: string;
  front: string;
  back: string;
  position: number;
}

/** Review quality mapped to rating (0-5 scale for SM-2) */
export const RATING_TO_QUALITY: Record<CardRating, number> = {
  [CardRating.AGAIN]: 1,
  [CardRating.HARD]: 3,
  [CardRating.GOOD]: 4,
  [CardRating.EASY]: 5,
};

// ============================================================
// Flashcard Source Links
// ============================================================

/** Link a flashcard to a DeckSource (markdown file) with optional heading anchor */
export interface FlashcardSourceLink {
  /** UUID of the DeckSourceEntity */
  sourceId: string;
  /** Heading text (e.g. "Installation") — optional, points to a sub-section */
  heading?: string;
  /** Heading slug for scroll (e.g. "installation") — optional */
  slug?: string;
}

// ============================================================
// Deck List (Collection)
// ============================================================

/** A user-defined collection of decks */
export interface DeckList {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  isPublic: boolean;
  deckCount: number;
  totalCardCount: number;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================
// Multiple Choice
// ============================================================

/** A single option in a multiple-choice flashcard */
export interface MultipleChoiceOption {
  /** Unique identifier (e.g. "A", "B", "C", "D") */
  id: string;
  /** Text content of the option */
  text: string;
  /** Whether this option is a correct answer — optional because it is stripped when sending to test-takers for security */
  isCorrect?: boolean;
}

// ============================================================
// Re-export lesson-related types
// ============================================================

export * from './lesson.types';
export * from './lesson-progress.types';